import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "../lib/auth";
import { Lock, Loader2, ArrowRight } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LOGO_URL } from "../lib/constants";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-50 flex items-center justify-center px-6 py-24" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img src={LOGO_URL} alt="Medstry" className="h-12 w-auto object-contain mb-6" />
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-teal-700">
            <Lock className="h-3.5 w-3.5" /> Admin Access
          </div>
        </div>

        <div className="bg-white border border-slate-200 p-8 md:p-10">
          <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-2">
            Sign in
          </h1>
          <p className="text-slate-500 text-sm mb-7">
            Internal dashboard for leads & quiz submissions.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-600 mb-2 block">
                Email
              </Label>
              <Input
                data-testid="admin-login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="contact@medstry.in"
                className="h-11 rounded-none border-slate-300 focus-visible:ring-[#5B6BD6]"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-600 mb-2 block">
                Password
              </Label>
              <Input
                data-testid="admin-login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-11 rounded-none border-slate-300 focus-visible:ring-[#5B6BD6]"
              />
            </div>

            {error && (
              <div data-testid="admin-login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="btn-primary px-6 py-3.5 text-sm font-semibold w-full inline-flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Signing in…
                </>
              ) : (
                <>
                  Sign in <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-xs text-slate-500 text-center mt-6">
          5 failed attempts will lock this account for 15 minutes.
        </p>
      </div>
    </main>
  );
}
